import { Loader2, Pencil, Search, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api } from "@/lib/api"
import type { Memory } from "@/lib/types"

/** Saved-facts management for one agent (the agent editor's Memory tab). */
export function MemoryTab({ agentId }: { agentId: string }) {
  const t = useT()
  const { locale } = useLocale()
  const [loading, setLoading] = React.useState(true)
  const [memories, setMemories] = React.useState<Memory[]>([])
  const [enabled, setEnabled] = React.useState(true)
  const [query, setQuery] = React.useState("")
  const [draft, setDraft] = React.useState("")
  const [saving, setSaving] = React.useState(false)
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [editText, setEditText] = React.useState("")

  const load = React.useCallback(async () => {
    try {
      const [agent, rows] = await Promise.all([api.getAgent(agentId), api.listMemories(agentId)])
      setEnabled(agent.memory_enabled)
      setMemories(rows)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("memory.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [agentId, t])

  React.useEffect(() => {
    void load()
  }, [load])

  async function toggle(next: boolean) {
    setEnabled(next)
    try {
      await api.updateAgent(agentId, { memory_enabled: next })
    } catch (error) {
      setEnabled(!next)
      toast.error(error instanceof ApiError ? error.message : t("memory.toasts.updateAgentFailed"))
    }
  }

  async function add() {
    const content = draft.trim()
    if (!content) return
    setSaving(true)
    try {
      const row = await api.createMemory(agentId, content)
      setMemories((current) => [row, ...current])
      setDraft("")
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("memory.toasts.saveFailed"))
    } finally {
      setSaving(false)
    }
  }

  async function saveEdit(id: string) {
    const content = editText.trim()
    if (!content) return
    try {
      const row = await api.updateMemory(agentId, id, content)
      setMemories((current) => current.map((memory) => (memory.id === id ? row : memory)))
      setEditingId(null)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("memory.toasts.editFailed"))
    }
  }

  async function remove(id: string) {
    if (!confirm(t("memory.deleteConfirm"))) return
    try {
      await api.deleteMemory(agentId, id)
      setMemories((current) => current.filter((memory) => memory.id !== id))
      toast.success(t("memory.toasts.deleted"))
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("memory.toasts.deleteFailed"))
    }
  }

  if (loading) {
    return <div className="text-muted-foreground p-1 text-sm">{t("memory.loading")}</div>
  }

  const needle = query.trim().toLowerCase()
  const visible = needle
    ? memories.filter((memory) => memory.content.toLowerCase().includes(needle))
    : memories

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <div>
            <CardTitle>{t("memory.title")}</CardTitle>
            <CardDescription>
              {t("memory.savedCount", { count: memories.length })}{" "}
              {enabled ? t("memory.hintEnabled") : t("memory.hintDisabled")}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="memory-enabled" className="text-xs font-normal">
              {t("common.enabled")}
            </Label>
            <Switch id="memory-enabled" checked={enabled} onCheckedChange={toggle} />
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("memory.addPlaceholder")}
            maxLength={1000}
            rows={2}
          />
          <Button type="button" onClick={() => void add()} disabled={saving || !draft.trim()}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("memory.saveButton")}
          </Button>
        </CardContent>
      </Card>

      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t("memory.searchPlaceholder")}
          className="pl-9"
        />
      </div>

      {visible.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {memories.length === 0
            ? t("memory.emptyNone")
            : t("memory.emptyNoMatch", { query })}
        </p>
      ) : (
        <div className="space-y-2">
          {visible.map((memory) => (
            <Card key={memory.id}>
              <CardContent className="flex items-start justify-between gap-3 py-3">
                {editingId === memory.id ? (
                  <div className="flex-1 space-y-2">
                    <Textarea
                      value={editText}
                      onChange={(event) => setEditText(event.target.value)}
                      maxLength={1000}
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => void saveEdit(memory.id)}>
                        {t("memory.editSaveButton")}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                        {t("memory.editCancelButton")}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <p className="text-sm">{memory.content}</p>
                    <p className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                      <Badge variant="outline">{memory.source}</Badge>
                      {new Date(memory.created_at).toLocaleDateString(locale)}
                    </p>
                  </div>
                )}
                {editingId !== memory.id && (
                  <div className="flex gap-1">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={t("memory.editAria")}
                      onClick={() => {
                        setEditingId(memory.id)
                        setEditText(memory.content)
                      }}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      aria-label={t("memory.deleteAria")}
                      onClick={() => void remove(memory.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
